import React, { useEffect, useState } from "react";
import { sitePath } from "../../lib/siteRouting";
import { Link, useParams } from "react-router-dom";
import publicApi, { formatError } from "../../api/public";
import SiteLayout from "../../components/site/SiteLayout";
import { Building2, MapPin, ArrowLeft, Send, Check, Calendar, Phone, Mail, Maximize, Car, Layers, Sparkles, Home as HomeIcon, BedDouble, Briefcase, TreePine } from "lucide-react";
import { toast } from "sonner";
import SEOHead from "../../components/site/SEOHead";
import { PhoneInput } from "../../components/PhoneInput";
import { CONTACT_PHONE, telLink } from "../../config/contact";

const ICON_MAP = { building: Building2, car: Car, layers: Layers, maximize: Maximize, sparkles: Sparkles, home: HomeIcon, bed: BedDouble, briefcase: Briefcase, tree: TreePine };
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SiteProjetDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [form, setForm] = useState({ nom: "", prenom: "", telephone: "", email: "", message: "", type: "contact" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    publicApi.get(`/projects/${slug}`)
      .then(({ data }) => setP(data))
      .catch(() => setP(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await publicApi.post("/contact", { ...form, project_slug: slug });
      setDone(true);
      toast.success("Demande envoyée !");
      setForm({ nom: "", prenom: "", telephone: "", email: "", message: "", type: "contact" });
    } catch (err) {
      toast.error(formatError(err, "Erreur d'envoi"));
    } finally { setSending(false); }
  };

  if (loading) return <SiteLayout><div className="py-32 text-center text-[#9CA3B5]">Chargement…</div></SiteLayout>;
  if (!p) return (
    <SiteLayout>
      <div className="max-w-[1400px] mx-auto px-6 py-32 text-center">
        <h1 className="font-display text-3xl font-bold mb-4">Projet introuvable</h1>
        <Link to={sitePath("/projets")} className="text-[#1E3A8A] hover:underline">← Retour aux projets</Link>
      </div>
    </SiteLayout>
  );

  const gallery = p.gallery || [];
  const mainImg = gallery[activeImg];

  return (
    <SiteLayout>
      <SEOHead
        title={p.nom}
        description={p.slogan || (p.description || "").slice(0, 160)}
        image={p.gallery && p.gallery[0] ? `${BACKEND_URL}${p.gallery[0].url}` : undefined}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Residence",
          "name": p.nom,
          "description": p.description_longue || p.slogan || "",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": p.adresse || "Hann Maristes 1, lot F/06/A",
            "addressLocality": p.ville || "Dakar",
            "addressCountry": "SN",
          },
          "image": p.gallery && p.gallery[0] ? `${BACKEND_URL}${p.gallery[0].url}` : undefined,
          "numberOfRooms": p.unites_summary?.length,
        }}
      />
      {/* Page title bar */}
      <div className="bg-[#F1F3FB] border-b border-[#E2E5F1] py-6">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 text-xs text-[#4B5775]">
            <Link to={sitePath("")} className="hover:text-[#1E3A8A]">Accueil</Link>
            <span>/</span>
            <Link to={sitePath("/projets")} className="hover:text-[#1E3A8A]">Nos projets</Link>
            <span>/</span>
            <span className="text-[#0F1A3D] font-semibold">{p.nom}</span>
          </div>
        </div>
      </div>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 relative">
        {/* Dot pattern decoration */}
        <div className="absolute top-12 left-0 w-32 h-32 hidden md:block opacity-40" style={{ backgroundImage: "radial-gradient(circle, #2E64A5 1.5px, transparent 1.5px)", backgroundSize: "12px 12px" }} />

        <div className="grid grid-cols-12 gap-8">
          {/* LEFT — vertical photo */}
          <div className="col-span-12 md:col-span-5 lg:col-span-4 relative">
            <div className="relative" data-testid="site-project-photo">
              {/* Vertical accent bar */}
              <div className="absolute -left-3 top-0 w-1 h-24 bg-[#D48A37] hidden md:block" />
              {mainImg ? (
                <img
                  src={`${BACKEND_URL}${mainImg.url}`}
                  alt={p.nom}
                  className="w-full aspect-[3/5] object-cover bg-[#F1F3FB]"
                />
              ) : (
                <div className="w-full aspect-[3/5] bg-gradient-to-br from-[#1E3A8A] to-[#3B5BBE] flex items-center justify-center">
                  <Building2 className="w-20 h-20 text-white/40" />
                </div>
              )}
              {gallery.length > 1 && (
                <div className="flex items-center gap-2 mt-3 overflow-x-auto">
                  {gallery.map((m, i) => (
                    <button
                      key={m.id}
                      onClick={() => setActiveImg(i)}
                      data-testid={`site-project-thumb-${i}`}
                      className={`shrink-0 w-16 h-20 overflow-hidden border-2 transition ${i === activeImg ? "border-[#E91E63]" : "border-transparent opacity-60 hover:opacity-100"}`}
                    >
                      <img src={`${BACKEND_URL}${m.url}`} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* CENTER — text */}
          <div className="col-span-12 md:col-span-7 lg:col-span-5">
            <div className="text-[14px] text-[#2E64A5] font-bold mb-3 tracking-wider">// NOS PROJETS</div>
            <h1 className="font-display text-[44px] lg:text-[56px] font-extrabold leading-[1.05] tracking-tight mb-8" style={{ fontFamily: "'Playfair Display', 'Georgia', serif" }} data-testid="site-project-title">
              {p.nom}
            </h1>
            <div className="space-y-5 text-[15px] text-[#4B5775] leading-[1.85] mb-8" data-testid="site-project-description">
              {(p.description_longue || p.description || "").split("\n").filter(Boolean).map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>

            {/* Unités du programme — décompte par typologie.
                Source de vérité : `p.unites_summary` (DB). Les typologies admin servent
                uniquement de meta (nom/surface/description/image). Plus de dépendance à `nb_unites`. */}
            {((p.typologies && p.typologies.length > 0) || (p.unites_summary && p.unites_summary.length > 0)) && (() => {
              // Grouper les unités réelles par code typologique (priorité à typologie_code, fallback surface)
              const buckets = {};
              for (const u of (p.unites_summary || [])) {
                const code = (u.typologie_code || "").toUpperCase() ||
                  (Number(u.surface_m2 || 0) >= 250 ? "T3" : Number(u.surface_m2 || 0) >= 170 ? "T2" : "T1");
                buckets[code] = buckets[code] || [];
                buckets[code].push(u);
              }
              // Construire les rows à partir des typologies admin (meta) ou des buckets réels
              const typosSorted = [...(p.typologies || [])].sort((a, b) => (a.code || "").localeCompare(b.code || ""));
              const codes = typosSorted.length > 0
                ? typosSorted.map(t => (t.code || "").toUpperCase() || "T?")
                : Object.keys(buckets).sort();
              const metaByCode = Object.fromEntries(typosSorted.map(t => [(t.code || "").toUpperCase(), t]));
              const rows = codes.map((code) => {
                const bucket = buckets[code] || [];
                const total = bucket.length;
                const dispoCount = bucket.filter((u) => u.statut === "disponible").length;
                const reserveCount = bucket.filter((u) => u.statut === "reserve").length;
                const venduCount = bucket.filter((u) => u.statut === "vendu").length;
                const m = metaByCode[code] || {};
                return {
                  code,
                  label: m.nom || `Type ${code.replace(/\D/g, "") || "?"}`,
                  surface: m.surface ? `${m.surface} m²` : (bucket[0]?.surface_m2 ? `${bucket[0].surface_m2} m²` : ""),
                  nbUnites: total,
                  dispoCount,
                  reserveCount,
                  venduCount,
                };
              }).filter((r) => r.nbUnites > 0);

              const totalUnites = rows.reduce((s, r) => s + r.nbUnites, 0);
              const totalDispo = rows.reduce((s, r) => s + r.dispoCount, 0);

              if (rows.length === 0) return null;

              return (
                <div className="mb-8 mt-12 pt-8 border-t border-[#E2E5F1]" data-testid="site-unites-section">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display text-xl font-bold text-[#0F1A3D]">Unités du programme</h3>
                    <span className="text-xs text-[#4B5775]">
                      <span className="font-mono font-bold text-[#1E3A8A]" data-testid="site-unites-count">{totalUnites}</span> au total
                      {totalDispo > 0 && <> · <span className="font-mono font-bold text-[#2D5A3C]" data-testid="site-unites-dispo">{totalDispo}</span> disponible{totalDispo > 1 ? "s" : ""}</>}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {rows.map((r) => (
                      <div key={r.code} className="flex items-center justify-between p-4 border border-[#E2E5F1] hover:border-[#1E3A8A] transition bg-white" data-testid={`site-typo-count-${r.code}`}>
                        <div>
                          <div className="font-semibold text-sm text-[#0F1A3D]">{r.label}</div>
                          {r.surface && <div className="text-xs text-[#4B5775] mt-0.5">Villa {r.surface}</div>}
                        </div>
                        <div className="text-right">
                          <div className="font-mono font-bold text-[#1E3A8A] text-lg leading-none">{r.nbUnites}</div>
                          <div className="text-[10px] uppercase tracking-wider text-[#4B5775] mt-1">
                            unité{r.nbUnites > 1 ? "s" : ""}
                            {r.dispoCount > 0 && <span className="text-[#2D5A3C] font-bold ml-1">· {r.dispoCount} dispo</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            <button
              onClick={() => document.getElementById("contact-form")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="site-project-cta-savoirplus"
              className="inline-flex items-center gap-2 h-14 px-10 bg-[#2E64A5] hover:bg-[#1E3A8A] text-white text-[13px] font-bold uppercase tracking-wider transition"
            >
              En savoir plus
            </button>
          </div>

          {/* RIGHT — blue sidebar with key figures */}
          <div className="col-span-12 lg:col-span-3 relative">
            <div className="bg-[#2E64A5] text-white p-7 -mt-8 sticky top-32 shadow-xl">
              {/* Address card on top */}
              <div className="text-center mb-2 pb-5 border-b border-white/20">
                <div className="text-[13px] leading-relaxed font-medium" data-testid="site-project-address">
                  {(p.adresse || "").split(",").map((line, i, arr) => (
                    <React.Fragment key={i}>
                      {line.trim()}{i < arr.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                  {p.ville && <><br />{p.ville}{p.pays ? ` ${p.pays}` : " Sénégal"}</>}
                </div>
              </div>
              {/* Key figures */}
              <div className="space-y-5 pt-5" data-testid="site-key-figures">
                {(p.key_figures || []).map((kf, i) => {
                  const Icon = ICON_MAP[kf.icon] || Sparkles;
                  return (
                    <div key={i} className="flex items-center gap-4" data-testid={`site-key-figure-${i}`}>
                      <div className="w-14 h-14 rounded-md bg-white text-[#E91E63] flex items-center justify-center shrink-0">
                        <Icon className="w-7 h-7" strokeWidth={1.5} />
                      </div>
                      <div className="leading-tight">
                        <div className="font-display text-2xl font-extrabold">{kf.value}</div>
                        <div className="text-[11px] uppercase tracking-wider text-white/80 mt-0.5">{kf.label}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Typologies (Villa Types) */}
        {p.typologies && p.typologies.length > 0 && (
          <div className="mt-20 pt-12 border-t border-[#E2E5F1]" data-testid="site-typologies-section">
            <div className="text-[14px] text-[#2E64A5] font-bold mb-3 tracking-wider">// TYPOLOGIES</div>
            <h2 className="font-display text-3xl lg:text-4xl font-extrabold mb-2 tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>Types de villas disponibles</h2>
            <p className="text-[#4B5775] text-[15px] mb-8">{p.typologies.length} typologie{p.typologies.length > 1 ? "s" : ""} au choix, du plus intimiste au plus spacieux.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {p.typologies.map((t, idx) => (
                <div key={t.code || idx} className="bg-white border border-[#E2E5F1] hover:border-[#2E64A5] transition group overflow-hidden" data-testid={`site-typologie-${t.code || idx}`}>
                  {t.image_url ? (
                    <div className="w-full h-64 overflow-hidden bg-[#F1F3FB]">
                      <img
                        src={t.image_url.startsWith("http") ? t.image_url : `${BACKEND_URL}${t.image_url}`}
                        alt={t.nom || t.code}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  ) : (
                    <div className="w-full h-64 bg-gradient-to-br from-[#F1F3FB] to-[#E2E5F1] flex items-center justify-center">
                      <HomeIcon className="w-16 h-16 text-[#9CA3B5]" />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-baseline justify-between mb-2">
                      <h3 className="font-display text-2xl font-extrabold text-[#0F1A3D]">{t.nom || `Villa ${t.code}`}</h3>
                      {t.code && <span className="text-xs font-mono font-bold text-[#E91E63] bg-[#FBEAE9] px-2 py-1 rounded">{t.code}</span>}
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-[#4B5775] mb-3">
                      {t.surface && <span className="inline-flex items-center gap-1"><Maximize className="w-3 h-3" /> {t.surface} m²</span>}
                      {t.nb_pieces && <span className="inline-flex items-center gap-1"><BedDouble className="w-3 h-3" /> {t.nb_pieces} pièces</span>}
                      {t.niveau && <span className="inline-flex items-center gap-1"><Layers className="w-3 h-3" /> {t.niveau}</span>}
                    </div>
                    {t.description && (
                      <p className="text-sm text-[#4B5775] leading-relaxed">{t.description}</p>
                    )}
                    {t.prix_a_partir_de && (
                      <div className="mt-4 pt-4 border-t border-[#E2E5F1] text-sm">
                        <span className="text-[#4B5775]">À partir de </span>
                        <span className="font-mono font-bold text-[#1E3A8A]">Prix sur demande</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Amenities */}
        {p.amenities && p.amenities.length > 0 && (
          <div className="mt-20 pt-12 border-t border-[#E2E5F1]">
            <div className="text-[14px] text-[#2E64A5] font-bold mb-3 tracking-wider">// ÉQUIPEMENTS</div>
            <h2 className="font-display text-3xl lg:text-4xl font-extrabold mb-8 tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>Aménités & services</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {p.amenities.map((a) => (
                <div key={a} className="flex items-center gap-2.5 px-4 py-3 bg-[#F8FAFC] border-l-4 border-[#E91E63]">
                  <Check className="w-4 h-4 text-[#2E64A5]" />
                  <span className="text-sm font-semibold">{a}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Contact form */}
        <div id="contact-form" className="mt-20 pt-12 border-t border-[#E2E5F1] grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="text-[14px] text-[#2E64A5] font-bold mb-3 tracking-wider">// CONTACT</div>
            <h2 className="font-display text-3xl lg:text-4xl font-extrabold leading-tight mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Intéressé par {p.nom} ?
            </h2>
            <p className="text-[#4B5775] leading-relaxed mb-6">Notre équipe commerciale vous recontacte sous 24h pour vous transmettre la fiche détaillée ou organiser une visite sur place.</p>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-3"><Phone className="w-4 h-4 text-[#E91E63]" /> <a href={telLink()} className="hover:text-[#1E3A8A]">{CONTACT_PHONE}</a></li>
              <li className="flex items-center gap-3"><Mail className="w-4 h-4 text-[#E91E63]" /> <a href="mailto:contact@tpco.sn" className="hover:text-[#1E3A8A]">contact@tpco.sn</a></li>
              <li className="flex items-start gap-3"><MapPin className="w-4 h-4 text-[#E91E63] mt-0.5" /> {p.adresse || "Dakar, Sénégal"}{p.ville && p.ville !== p.adresse ? `, ${p.ville}` : ""}{p.pays ? `, ${p.pays}` : ""}</li>
            </ul>
          </div>
          <div className="bg-[#F8FAFC] p-8 border border-[#E2E5F1]" data-testid="site-contact-form">
            {done ? (
              <div className="text-center py-8">
                <div className="w-14 h-14 mx-auto rounded-full bg-[#E5F0E8] text-[#2D5A3C] flex items-center justify-center mb-4"><Check className="w-7 h-7" /></div>
                <h3 className="font-display text-2xl font-bold mb-2">Merci !</h3>
                <p className="text-sm text-[#4B5775]">Votre demande a bien été reçue. Notre équipe vous recontacte sous 24h.</p>
                <button onClick={() => setDone(false)} className="text-xs text-[#1E3A8A] font-semibold hover:underline mt-4">Envoyer une autre demande</button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3">
                <div className="flex gap-2 mb-3">
                  <button type="button" onClick={() => setForm({ ...form, type: "contact" })} className={`flex-1 h-10 text-xs font-bold uppercase tracking-wider ${form.type === "contact" ? "bg-[#2E64A5] text-white" : "bg-white border border-[#E2E5F1] text-[#4B5775]"}`} data-testid="site-contact-type-info">Demander la fiche</button>
                  <button type="button" onClick={() => setForm({ ...form, type: "visite" })} className={`flex-1 h-10 text-xs font-bold uppercase tracking-wider ${form.type === "visite" ? "bg-[#E91E63] text-white" : "bg-white border border-[#E2E5F1] text-[#4B5775]"}`} data-testid="site-contact-type-visite"><Calendar className="w-3 h-3 inline mr-1" /> Demander visite</button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input required value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} placeholder="Nom *" className="w-full h-11 px-3 text-sm border border-[#E2E5F1] focus:outline-none focus:border-[#1E3A8A] bg-white" data-testid="site-contact-nom" />
                  <input value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} placeholder="Prénom" className="w-full h-11 px-3 text-sm border border-[#E2E5F1] focus:outline-none focus:border-[#1E3A8A] bg-white" />
                </div>
                <PhoneInput required value={form.telephone} onChange={(v) => setForm({ ...form, telephone: v })} testid="site-contact-telephone" placeholder="77 123 45 67" />
                <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" type="email" className="w-full h-11 px-3 text-sm border border-[#E2E5F1] focus:outline-none focus:border-[#1E3A8A] bg-white" />
                <textarea required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={form.type === "visite" ? "Date souhaitée, créneau préféré…" : "Votre message…"} rows={4} className="w-full px-3 py-2 text-sm border border-[#E2E5F1] focus:outline-none focus:border-[#1E3A8A] resize-none bg-white" data-testid="site-contact-message" />
                <button type="submit" disabled={sending} data-testid="site-contact-submit" className="w-full inline-flex items-center justify-center gap-2 h-12 bg-[#E91E63] hover:bg-[#D81B60] text-white text-xs font-bold uppercase tracking-wider disabled:opacity-60">
                  <Send className="w-4 h-4" /> {sending ? "Envoi…" : "Envoyer ma demande"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
